import React, { useCallback, useRef, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarIcon, ChevronDown, Loader2, Plus, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  format,
  differenceInDays,
  isAfter,
  startOfDay,
  addDays,
  addMonths,
} from 'date-fns';
import { SelectValue } from '@radix-ui/react-select';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import createApiClient from '@/api/axiosInstance';
import { createProjectAction } from '@/js/reduxstore/actions/projectActions';
import { projectCreationDump } from '@/js/reduxstore/actions/dumpActions';

const formSchema = z
  .object({
    project_name: z.string().min(1, 'Project name is required'),
    start_date: z
      .date({
        required_error: 'Start date is required',
        invalid_type_error: 'Invalid start date',
      })
      .min(startOfDay(new Date()), 'Start date cannot be in the past')
      .max(
        addMonths(new Date(), 6),
        'Start date cannot be more than 6 months in the future',
      ),
    due_date: z
      .date({
        required_error: 'Due date is required',
        invalid_type_error: 'Invalid due date',
      })
      .min(startOfDay(new Date()), 'Due date cannot be in the past'),
    description: z.string().min(1, 'Description is required'),
    certifications_required: z
      .array(z.string())
      .min(1, 'At least one certifications_required is required')
      .refine((data) => data.every((item) => item.trim().length > 0), {
        message: 'certifications_required cannot be empty strings',
      }),
    preferred_manufacturer_location: z
      .array(z.string())
      .min(1, 'At least one manufacturer location is required')
      .refine((data) => data.every((item) => item.trim().length > 0), {
        message: 'Manufacturer locations cannot be empty strings',
      }),
    // quantity: z
    //   .number()
    //   .int('Quantity must be a whole number')
    //   .min(1, 'Quantity is required')
    //   .positive('Quantity must be positive'),
    // itemLength: z
    //   .string()
    //   .min(1, 'Item length is required')
    //   .refine(
    //     (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    //     'Length must be a valid positive number',
    //   ),
    // itemWidth: z
    //   .string()
    //   .min(1, 'Item width is required')
    //   .refine(
    //     (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    //     'Width must be a valid positive number',
    //   ),
    // itemHeight: z
    //   .string()
    //   .min(1, 'Item height is required')
    //   .refine(
    //     (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    //     'Height must be a valid positive number',
    //   ),
    // dimensionUom: z.string().min(1, 'UOM is required'),
    // itemWeight: z
    //   .string()
    //   .min(1, 'Weight is required')
    //   .refine(
    //     (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    //     'Weight must be a valid positive number',
    //   ),
    // weightUom: z.string().min(1, 'Weight UOM is required'),
    // requiredMaterials: z
    //   .array(z.string())
    //   .min(1, 'At least one material is required'),
    // processesNeeded: z
    //   .array(z.string())
    //   .min(1, 'At least one process is required'),
  })
  .refine(
    (data) => {
      if (!data.start_date || !data.due_date) return true;
      const isValidRange = isAfter(data.due_date, data.start_date);
      const minDuration = differenceInDays(data.due_date, data.start_date) >= 1;
      const maxDuration =
        differenceInDays(data.due_date, data.start_date) <= 365;
      return isValidRange && minDuration && maxDuration;
    },
    {
      message:
        'Invalid date range. Project duration must be between 1 day and 1 year, with due date after start date.',
      path: ['due_date'],
    },
  );

export const CreateProjectScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const preferred_manufacturer_location_ref = useRef(null);
  const certifications_required_ref = useRef(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      project_name: '',
      start_date: undefined,
      due_date: undefined,
      description: '',
      certifications_required: [],
      preferred_manufacturer_location: [],
    },
  });

  const start_date = useWatch({ control: form.control, name: 'start_date' });
  const due_date = useWatch({ control: form.control, name: 'due_date' });

  // Calculate project duration only if both dates are valid
  const estimated_project_duration = React.useMemo(() => {
    if (!start_date || !due_date) return '';
    const days = differenceInDays(due_date, start_date);
    return days < 0 ? '' : `${days} days`;
  }, [start_date, due_date]);

  // Function to disable invalid dates in the calendar
  const disableInvalidDates = (date) => {
    const today = startOfDay(new Date());
    const sixMonthsFromNow = addMonths(today, 6);
    return date < today || date > sixMonthsFromNow;
  };

  // Function to disable invalid due dates based on selected start date
  const disableInvaliddue_dates = (date) => {
    const today = startOfDay(new Date());
    if (date < today) return true;
    if (!start_date) return false;

    const oneYearFromStart = addDays(start_date, 365);
    const dayBeforeStart = addDays(start_date, 0);

    return date <= dayBeforeStart || date > oneYearFromStart;
  };

  const handleKeyPress = useCallback((e, field, setValue) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value) {
        const currentValues = field.value || [];
        if (!currentValues.includes(value)) {
          setValue([...currentValues, value]);
          e.target.value = '';
        }
      }
    }
  }, []);

  const handleRemoveItem = useCallback((index, field, setValue) => {
    const newValues = [...field.value];
    newValues.splice(index, 1);
    setValue(newValues);
  }, []);

  const onSubmit = async (values) => {
    setIsLoading(true);

    const formattedValues = {
      ...values,
      start_date: values.start_date
        ? format(new Date(values.start_date), 'yyyy-MM-dd')
        : '',
      due_date: values.due_date
        ? format(new Date(values.due_date), 'yyyy-MM-dd')
        : '',
      estimated_project_duration: estimated_project_duration,
      deadline_quote_submission: values.due_date
        ? format(new Date(values.due_date), 'yyyy-MM-dd')
        : '',
      status: 'draft',
    };

    try {
      const response = await apiClient.post(
        '/api/project/create',
        JSON.stringify(formattedValues),
      );
      const responseData = response.data;
      console.log('✅ Project creation successful! with data:', responseData);

      setError(null);
      dispatch(createProjectAction(responseData.data));
      dispatch(projectCreationDump(responseData.data));
      navigate(
        `/project/add-parts/${responseData.data.project_name}/${responseData.data.project_id}`,
      );
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.log(error_message || error);

      setError(
        error_message ||
          'A network error occurred. Please check your connection and try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg font-roboto">
      <div className="py-6 px-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                e.target.tagName === 'INPUT' &&
                e.target.tagName !== 'TEXTAREA' &&
                !e.target.getAttribute('data-allow-enter')
              ) {
                e.preventDefault();
              }
            }}
            className="space-y-4"
          >
            {/* general information */}
            <div>
              <p className="text-darkPurple font-medium mb-8">
                General Information
              </p>
              {/* Project Name */}
              <FormField
                control={form.control}
                name="project_name"
                render={({ field }) => (
                  <FormItem className="w-1/2 my-6 group">
                    <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                      Project Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none  placeholder:text-slate-500 border border-gray-300 group-hover:border-darkPurple group-hover:placeholder:text-black"
                        placeholder="Name of product"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-x-4 my-4">
                {/* Start Date with disabled dates */}
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem className="group">
                      <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                        Start Date
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={`w-full flex justify-between group-hover:bg-veryLightPurple group-hover:bg-opacity-15 ease-in duration-150 group-hover:text-black ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border border-gray-300`}
                            >
                              {field.value
                                ? format(field.value, 'PPP')
                                : 'Select the project start date'}
                              <CalendarIcon className="ml-2 h-4 w-4 text-darkPurple" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Calendar
                            mode="single"
                            classNames={{
                              caption_label:
                                'text-sm font-medium text-gray-600',
                              nav: 'space-x-1 flex items-center',
                              nav_button:
                                'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                              nav_button_previous: 'absolute left-1',
                              nav_button_next: 'absolute right-1',
                              table: 'w-full border-collapse space-y-1',
                              head_row:
                                'flex bg-veryLightPurple bg-opacity-15 rounded',
                              head_cell:
                                'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                              row: 'flex w-full mt-2',
                              cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-purple-50',
                              day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-purple-50 hover:text-purple-700',
                              day_selected:
                                'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                              day_today:
                                'bg-gray-100 text-gray-900 font-semibold',
                              day_outside:
                                'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                              day_disabled:
                                'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                              day_range_middle:
                                'aria-selected:bg-purple-50 aria-selected:text-purple-900',
                              day_hidden: 'invisible',
                            }}
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={disableInvalidDates}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Due Date with disabled dates */}
                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem className="group">
                      <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                        Due Date
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={`w-full flex justify-between group-hover:bg-veryLightPurple group-hover:bg-opacity-15 ease-in duration-150 group-hover:text-black ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border border-gray-300`}
                            >
                              {field.value
                                ? format(field.value, 'PPP')
                                : 'Select the project due date'}
                              <CalendarIcon className="ml-2 h-4 w-4 text-darkPurple" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Calendar
                            mode="single"
                            classNames={{
                              caption_label:
                                'text-sm font-medium text-gray-600',
                              nav: 'space-x-1 flex items-center',
                              nav_button:
                                'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                              nav_button_previous: 'absolute left-1',
                              nav_button_next: 'absolute right-1',
                              table: 'w-full border-collapse space-y-1',
                              head_row:
                                'flex bg-veryLightPurple bg-opacity-15 rounded',
                              head_cell:
                                'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                              row: 'flex w-full mt-2',
                              cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-purple-50',
                              day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-purple-50 hover:text-purple-700',
                              day_selected:
                                'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                              day_today:
                                'bg-gray-100 text-gray-900 font-semibold',
                              day_outside:
                                'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                              day_disabled:
                                'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                              day_range_middle:
                                'aria-selected:bg-purple-50 aria-selected:text-purple-900',
                              day_hidden: 'invisible',
                            }}
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={disableInvaliddue_dates}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Estimated Project Duration */}
                <FormItem className="">
                  <FormLabel className="text-gray-700">
                    Estimated Project Duration (days)
                  </FormLabel>
                  <FormControl>
                    <Input
                      value={estimated_project_duration}
                      placeholder="Duration (auto)"
                      disabled
                      className="border border-gray-300"
                    />
                  </FormControl>
                </FormItem>
              </div>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="my-6 group">
                    <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                      Description
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-darkPurple group-hover:placeholder:text-black "
                        placeholder="Describe the product"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* certifications_required */}
              <FormField
                control={form.control}
                name="certifications_required"
                render={({ field }) => (
                  <FormItem className="w-1/2 my-6 group">
                    <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                      Certifications Required
                    </FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <div className="relative">
                          <Input
                            placeholder="Add Certificates (e.g., ISO 9001, FDA)"
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-darkPurple group-hover:placeholder:text-black "
                            onKeyPress={(e) =>
                              handleKeyPress(
                                e,
                                field,
                                form.setValue.bind(
                                  null,
                                  'certifications_required',
                                ),
                              )
                            }
                            ref={certifications_required_ref}
                            data-allow-enter
                          />
                          <Plus
                            className="w-6 h-6 absolute top-2 right-2 text-darkPurple cursor-pointer"
                            onClick={() => {
                              const input = certifications_required_ref.current;
                              if (input && input.value.trim() !== '') {
                                handleKeyPress(
                                  {
                                    key: 'Enter',
                                    target: input,
                                    preventDefault: () => {},
                                  },
                                  field,
                                  form.setValue.bind(
                                    null,
                                    'certifications_required',
                                  ),
                                );
                              }
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {field.value?.map((cert, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1 px-3 py-1 bg-purple-100 rounded-md"
                            >
                              <span className="text-gray-500 font-medium text-sm">
                                {cert}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveItem(
                                    index,
                                    field,
                                    form.setValue.bind(
                                      null,
                                      'certifications_required',
                                    ),
                                  )
                                }
                                className="text-purple-700 hover:text-purple-900"
                              >
                                <X className="h-4 w-4 text-gray-500 font-medium" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Preferred Manufacturer Location */}
              <FormField
                control={form.control}
                name="preferred_manufacturer_location"
                render={({ field }) => (
                  <FormItem className="w-1/2 group">
                    <FormLabel className="text-gray-700 group-hover:text-darkPurple">
                      Preferred Manufacturer Location
                    </FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <div className="relative">
                          <Input
                            placeholder="Add locations (e.g., North America, Asia)"
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-darkPurple group-hover:placeholder:text-black "
                            onKeyPress={(e) =>
                              handleKeyPress(
                                e,
                                field,
                                form.setValue.bind(
                                  null,
                                  'preferred_manufacturer_location',
                                ),
                              )
                            }
                            ref={preferred_manufacturer_location_ref}
                            data-allow-enter
                          />
                          <Plus
                            className="w-6 h-6 absolute top-2 right-2 text-darkPurple cursor-pointer"
                            onClick={() => {
                              const input =
                                preferred_manufacturer_location_ref.current;
                              if (input && input.value.trim() !== '') {
                                handleKeyPress(
                                  {
                                    key: 'Enter',
                                    target: input,
                                    preventDefault: () => {},
                                  },
                                  field,
                                  form.setValue.bind(
                                    null,
                                    'preferred_manufacturer_location',
                                  ),
                                );
                              }
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {field.value?.map((location, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1 px-3 py-1 bg-purple-100 rounded-md"
                            >
                              <span className="text-gray-500 font-medium text-sm">
                                {location}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveItem(
                                    index,
                                    field,
                                    form.setValue.bind(
                                      null,
                                      'preferred_manufacturer_location',
                                    ),
                                  )
                                }
                                className="text-purple-700 hover:text-purple-900"
                              >
                                <X className="h-4 w-4 text-gray-500 font-medium" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {error && (
              <div className="w-full flex items-center justify-center mt-4 ">
                <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                  {error}
                </p>
              </div>
            )}

            <div className="w-full flex items-center justify-center">
              <Button
                variant="outline"
                type="submit"
                disabled={isLoading}
                className={`${error ? '' : 'mt-4'} text-white hover:text-white transition ease-in-out duration-300 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-darkPurple hover:bg-darkerPurple'
                } flex items-center justify-center`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Save & Add Part Details'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
